
import React, { useState } from 'react';
import { Identity } from '../types';
import { translations, Language } from '../translations';
import { 
  MapPin, Phone, Mail, Briefcase, CreditCard as CardIcon, 
  Calendar, Fingerprint, Copy, Check, Info, ExternalLink, 
  Sparkles, Share2
} from 'lucide-react';

interface Props {
  identity: Identity;
  lang: Language;
}

const IdentityCard: React.FC<Props> = ({ identity, lang }) => {
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const t = translations[lang];

  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const InfoBlock = ({ label, value, field, icon: Icon }: any) => (
    <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 group relative hover:border-blue-400 transition-all hover:shadow-sm">
      <div className="flex items-center gap-3">
        <div className="flex-shrink-0 p-2 bg-white rounded-lg text-slate-900 group-hover:text-blue-800 shadow-sm transition-colors w-9 h-9 flex items-center justify-center border border-slate-200">
          <Icon size={18} />
        </div>
        <div className="flex-1 overflow-hidden">
          <p className="text-[10px] font-bold text-slate-800 uppercase tracking-widest">{label}</p>
          <p className="text-sm font-semibold text-slate-950 truncate">{value}</p>
        </div>
        <button 
          onClick={() => copyToClipboard(value, field)}
          className="p-1.5 text-slate-500 hover:text-blue-800 transition-colors"
          aria-label={`复制 ${label}`}
        >
          {copiedField === field ? <Check size={14} className="text-green-700" /> : <Copy size={14} />}
        </button>
      </div>
    </div>
  );

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500" style={{ contain: 'layout' }}>
      <div className="bg-white rounded-[2.5rem] border border-slate-300 shadow-2xl overflow-hidden mb-6 contain-paint">
        {/* Header - 固定高度防止重排 */}
        <div className="h-32 bg-gradient-to-r from-blue-800 to-indigo-900 relative">
          <div className="absolute -bottom-12 left-8 p-1 bg-white rounded-[2rem] shadow-xl">
            <div className="w-24 h-24 bg-blue-50 rounded-[1.8rem] flex items-center justify-center text-3xl font-black text-blue-800 shadow-inner border border-blue-100 overflow-hidden aspect-square">
              {identity.name.charAt(0)}
            </div>
          </div>
        </div>

        <div className="pt-16 pb-8 px-8">
          <div className="flex justify-between items-start mb-8 gap-4">
            <div className="min-w-0">
              <h2 className="text-2xl md:text-3xl font-black text-slate-900 truncate">{identity.name}</h2>
              <div className="flex flex-wrap gap-2 mt-2">
                <span className="px-2 py-0.5 bg-blue-50 text-blue-900 rounded text-[10px] font-bold uppercase border border-blue-300">{identity.gender}</span>
                <span className="px-2 py-0.5 bg-indigo-50 text-indigo-900 rounded text-[10px] font-bold uppercase border border-indigo-300">{identity.age} 岁</span>
                <span className="px-2 py-0.5 bg-slate-100 text-slate-900 rounded text-[10px] font-bold uppercase border border-slate-400">{identity.address.country}</span>
              </div>
            </div>
            <button className="flex-shrink-0 p-3 bg-slate-900 text-white rounded-2xl hover:bg-slate-800 transition-all shadow-lg active:scale-95">
              <Share2 size={20} />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 min-h-[160px]">
            <InfoBlock label="生日" value={identity.birthday} field="birthday" icon={Calendar} />
            <InfoBlock label="职业" value={identity.job} field="job" icon={Briefcase} />
            <InfoBlock label="联系电话" value={identity.phone} field="phone" icon={Phone} />
            <InfoBlock label="电子邮箱" value={identity.email} field="email" icon={Mail} />
            <InfoBlock label="身份识别" value={identity.idNumber} field="idnum" icon={Fingerprint} />
            <InfoBlock label="所在公司" value={identity.company} field="company" icon={Info} />
          </div>

          <div className="mt-6 p-4 rounded-2xl bg-blue-100/40 border border-blue-300 transition-colors hover:bg-blue-100/60 min-h-[72px]">
            <div className="flex items-start gap-4">
              <div className="p-2 bg-white rounded-lg text-blue-900 shadow-sm border border-blue-200">
                <MapPin size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-bold text-blue-900 uppercase tracking-widest">详细地址</p>
                <p className="text-sm font-semibold text-slate-950 leading-relaxed mt-0.5">
                  {identity.address.street}, {identity.address.city}, {identity.address.state} {identity.address.zipCode}
                </p>
              </div>
              <a 
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${identity.address.street}, ${identity.address.city}`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 text-blue-900 hover:bg-white rounded-xl transition-all border border-transparent hover:border-blue-300"
                aria-label="在地图中查看"
              >
                <ExternalLink size={18} />
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 pb-8">
        <section className="bg-white p-8 rounded-[2rem] border border-slate-300 shadow-sm transition-transform hover:-translate-y-1 duration-300">
          <h3 className="font-bold text-slate-900 flex items-center gap-2 mb-6">
            <CardIcon size={20} className="text-blue-800" /> {t.ccInfo}
          </h3>
          <div className="p-6 rounded-3xl bg-slate-950 text-white relative overflow-hidden shadow-xl aspect-[1.58/1] contain-content">
             <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -mr-16 -mt-16"></div>
             <p className="text-[10px] font-mono text-slate-200 mb-6 uppercase tracking-widest font-black">Virtual Card Service</p>
             <p className="text-xl md:text-2xl font-mono tracking-widest mb-8">{identity.creditCard.number}</p>
             <div className="flex justify-between items-end">
               <div className="flex gap-6">
                 <div>
                   <p className="text-[8px] text-slate-300 uppercase font-black">Expires</p>
                   <p className="text-sm font-mono">{identity.creditCard.expiry}</p>
                 </div>
                 <div>
                   <p className="text-[8px] text-slate-300 uppercase font-black">CVV</p>
                   <p className="text-sm font-mono">{identity.creditCard.cvv}</p>
                 </div>
               </div>
               <div className="w-12 h-8 bg-gradient-to-tr from-yellow-700 to-yellow-500 rounded-lg opacity-95 shadow-inner"></div>
             </div>
          </div>
        </section>

        <section className="bg-white p-8 rounded-[2rem] border border-slate-300 shadow-sm flex flex-col transition-transform hover:-translate-y-1 duration-300">
          <h3 className="font-bold text-slate-900 flex items-center gap-2 mb-4">
            <Sparkles size={20} className="text-indigo-800" /> {t.bio}
          </h3>
          <div className="flex-1 overflow-auto custom-scrollbar min-h-[100px]">
            <p className="text-slate-950 text-sm leading-relaxed italic border-l-4 border-indigo-300 pl-4 py-1">
              &ldquo;{identity.bio}&rdquo;
            </p>
          </div>
        </section>
      </div>
    </div>
  );
};

export default IdentityCard;
